import { BrowserRouter, Routes, Route, Link, useLocation } from 'react-router-dom'
import { ThemeProvider } from './components/theme-provider'
import { ErrorBoundary } from './components/ErrorBoundary'
import { useState, lazy, Suspense, useEffect } from 'react'
import { HelpCircle, Settings } from 'lucide-react'
import { useWelcomeDialog } from './hooks/useWelcomeDialog'
import { WelcomeDialogContext, useWelcomeDialogContext } from './contexts/WelcomeDialogContext'

// Lazy load page components for code splitting
const SubmissionPage = lazy(() => import('./pages/SubmissionPage'))
const DataTablePage = lazy(() => import('./pages/DataTablePage'))
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'))
const ResourcesPage = lazy(() => import('./pages/ResourcesPage'))
const AboutPage = lazy(() => import('./pages/AboutPage'))
const SettingsPage = lazy(() => import('./pages/SettingsPage'))

function Navigation({
  isCollapsed,
  setIsCollapsed,
}: {
  isCollapsed: boolean
  setIsCollapsed: (value: boolean) => void
}): React.JSX.Element {
  const [isOpen, setIsOpen] = useState(false)
  const location = useLocation()
  const { resetWelcome, setShowWelcome } = useWelcomeDialogContext()

  // Skip navigation link for keyboard users
  const skipToMain = () => {
    const main = document.querySelector('main')
    if (main) {
      main.focus()
      main.scrollIntoView()
    }
  }

  const navItems = [
    { path: '/', label: 'Daily Submission', icon: '📝' },
    { path: '/data', label: 'Data Table', icon: '📊' },
    { path: '/analytics', label: 'Analytics', icon: '📈' },
    { path: '/resources', label: 'Resources', icon: '📖' },
    { path: '/about', label: 'About', icon: 'ℹ️' },
  ]

  return (
    <>
      {/* Skip to main content link for keyboard navigation */}
      <a
        href="#main-content"
        onClick={(e) => {
          e.preventDefault()
          skipToMain()
        }}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[60] focus:px-4 focus:py-2 focus:bg-primary focus:text-primary-foreground focus:rounded-md focus:outline-none focus:ring-2 focus:ring-ring"
      >
        Skip to main content
      </a>

      {/* Mobile menu button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        aria-label={isOpen ? 'Close navigation menu' : 'Open navigation menu'}
        aria-expanded={isOpen}
        aria-controls="main-navigation"
        className="fixed top-4 left-4 z-50 md:hidden p-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
      >
        <span aria-hidden="true">{isOpen ? '✕' : '☰'}</span>
      </button>

      {/* Navigation drawer */}
      <nav
        id="main-navigation"
        aria-label="Main navigation"
        className={`
          fixed top-0 left-0 h-full bg-card border-r border-border z-40
          transform transition-all duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
          ${isCollapsed ? 'w-20' : 'w-64'}
          md:translate-x-0
        `}
        style={{ backgroundColor: 'hsl(var(--card))' }}
      >
        <div className="p-6 flex flex-col h-full">
          <div className="mb-8">
            <Link
              to="/about"
              className="block focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 rounded-md"
              aria-label="AttraFlow home"
            >
              {isCollapsed ? (
                <img
                  src="/logo.png"
                  alt="AttraFlow logo"
                  className="h-14 w-14 mx-auto object-contain"
                  aria-hidden="false"
                />
              ) : (
                <img
                  src="/logo-full.png"
                  alt="AttraFlow - Track the Flow of Your Attraction"
                  className="h-20 w-full object-contain px-2"
                  aria-hidden="false"
                />
              )}
            </Link>
          </div>

          <ul className="space-y-2 flex-1" role="list">
            {navItems.map((item) => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => setIsOpen(false)}
                  className={`
                    flex items-center gap-3 px-4 py-3 rounded-md transition-colors
                    focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2
                    ${isCollapsed ? 'justify-center' : ''}
                    ${
                      location.pathname === item.path
                        ? 'bg-primary text-primary-foreground'
                        : 'hover:bg-accent hover:text-accent-foreground'
                    }
                  `}
                  aria-label={item.label}
                  aria-current={location.pathname === item.path ? 'page' : undefined}
                  title={isCollapsed ? item.label : ''}
                >
                  <span className="text-xl" aria-hidden="true">
                    {item.icon}
                  </span>
                  {!isCollapsed && <span className="font-medium">{item.label}</span>}
                </Link>
              </li>
            ))}
          </ul>

          {/* Help button */}
          <div
            className={`border-t border-border pt-4 ${isCollapsed ? 'px-2' : 'px-4'}`}
            role="region"
            aria-label="Help"
          >
            {isCollapsed ? (
              <button
                onClick={() => {
                  resetWelcome()
                  setShowWelcome(true)
                }}
                className="w-full p-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                title="Show welcome guide"
                aria-label="Show welcome guide"
              >
                <HelpCircle className="h-5 w-5" aria-hidden="true" />
              </button>
            ) : (
              <button
                onClick={() => {
                  resetWelcome()
                  setShowWelcome(true)
                }}
                className="w-full flex items-center gap-2 px-3 py-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                aria-label="Show welcome guide"
              >
                <HelpCircle className="h-4 w-4" aria-hidden="true" />
                <span className="font-medium">Welcome Guide</span>
              </button>
            )}
          </div>

          {/* Settings button */}
          <div
            className={`border-t border-border pt-4 ${isCollapsed ? 'px-2' : 'px-4'}`}
            role="region"
            aria-label="Settings"
          >
            {isCollapsed ? (
              <Link
                to="/settings"
                className="w-full p-3 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors flex items-center justify-center focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                title="Settings"
                aria-label="Open settings"
              >
                <Settings className="h-5 w-5" aria-hidden="true" />
              </Link>
            ) : (
              <Link
                to="/settings"
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 ${
                  location.pathname === '/settings'
                    ? 'bg-primary text-primary-foreground'
                    : 'hover:bg-accent hover:text-accent-foreground'
                }`}
                aria-current={location.pathname === '/settings' ? 'page' : undefined}
              >
                <Settings className="h-5 w-5 shrink-0" aria-hidden="true" />
                <span className="font-medium">Settings</span>
              </Link>
            )}
          </div>

          {/* Desktop collapse toggle button */}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="hidden md:flex items-center justify-center p-3 mt-2 rounded-md hover:bg-accent hover:text-accent-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
            aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            aria-expanded={!isCollapsed}
            aria-controls="main-navigation"
          >
            <span className="text-xl" aria-hidden="true">
              {isCollapsed ? '→' : '←'}
            </span>
            {!isCollapsed && <span className="ml-2 font-medium">Collapse</span>}
          </button>
        </div>
      </nav>

      {/* Overlay for mobile */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setIsOpen(false)}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setIsOpen(false)
          }}
          role="button"
          tabIndex={0}
          aria-label="Close navigation overlay"
        />
      )}
    </>
  )
}

function AppContent(): React.JSX.Element {
  const [isCollapsed, setIsCollapsed] = useState(false)
  const { isFirstVisit, dismissWelcome, resetWelcome } = useWelcomeDialog()
  const [showWelcome, setShowWelcome] = useState(false)

  // Sync showWelcome with isFirstVisit
  useEffect(() => {
    setShowWelcome(isFirstVisit)
  }, [isFirstVisit])

  const contextValue = {
    showWelcome,
    setShowWelcome,
    dismissWelcome: () => {
      dismissWelcome()
      setShowWelcome(false)
    },
    resetWelcome,
  }

  return (
    <WelcomeDialogContext.Provider value={contextValue}>
      <div className="min-h-screen bg-background">
        <Navigation isCollapsed={isCollapsed} setIsCollapsed={setIsCollapsed} />

        {/* Main content with offset for sidebar */}
        <main
          id="main-content"
          className={`transition-all duration-300 min-h-screen ${isCollapsed ? 'md:ml-20' : 'md:ml-64'}`}
          tabIndex={-1}
          role="main"
          aria-label="Main content"
        >
          <Suspense
            fallback={
              <div className="flex items-center justify-center min-h-screen">
                <div className="text-center">
                  <div
                    className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                    role="status"
                  >
                    <span className="sr-only">Loading...</span>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">Loading page...</p>
                </div>
              </div>
            }
          >
            <Routes>
              <Route path="/" element={<SubmissionPage />} />
              <Route path="/data" element={<DataTablePage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/resources" element={<ResourcesPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/settings" element={<SettingsPage />} />
            </Routes>
          </Suspense>
        </main>
      </div>
    </WelcomeDialogContext.Provider>
  )
}

function App(): React.JSX.Element {
  return (
    <ErrorBoundary>
      <ThemeProvider defaultTheme="system" storageKey="attraflow-theme">
        <BrowserRouter>
          <AppContent />
        </BrowserRouter>
      </ThemeProvider>
    </ErrorBoundary>
  )
}

export default App
